import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, Users, FileText, Search, ArrowRight, ScanFace, ChevronRight, MapPin, Phone, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const [stats, setStats] = useState({
    total_missing: 0,
    total_unknown: 0,
    pending_approval: 0,
    approved_cases: 0,
    states_data: []
  });
  const [shokhCount, setShokhCount] = useState(0);
  const [shokhItems, setShokhItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const getPhotoUrl = (path) => path ? (path.startsWith('http') ? path : `${API}/api/files/${path}`) : null;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, shokhRes, shokhListRes] = await Promise.all([
        axios.get(`${API}/api/stats`),
        axios.get(`${API}/api/shokh-stats`).catch(() => ({ data: { total_approved: 0 } })),
        axios.get(`${API}/api/shokh-sandesh?page=1&limit=4`).catch(() => ({ data: { items: [] } }))
      ]);
      setStats(statsRes.data);
      setShokhCount(shokhRes.data.total_approved);
      setShokhItems(shokhListRes.data.items || []);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div data-testid="home-page">
      {/* ===== DESKTOP HERO ===== */}
      <section className="hidden md:block desktop-hero py-20 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full text-red-400 text-sm font-medium mb-6">
                <AlertTriangle className="w-4 h-4" />
                राष्ट्रीय लापता व्यक्ति पोर्टल
              </motion.div>
              
              <motion.h1 variants={itemVariants} className="font-outfit font-black text-5xl lg:text-6xl text-white leading-tight mb-6">
                परिवारों को मिलाने में<br />
                <span className="text-red-500">मदद करें</span>
              </motion.h1>
              
              <motion.p variants={itemVariants} className="text-gray-400 text-lg max-w-lg mb-8 leading-relaxed">
                हर जानकारी महत्वपूर्ण है। साथ मिलकर हम लापता व्यक्तियों को घर वापस ला सकते हैं और अज्ञात व्यक्तियों की पहचान कर सकते हैं।
              </motion.p>
              
              <motion.div variants={itemVariants} className="flex flex-wrap gap-4">
                <Link to="/submit">
                  <Button size="lg" className="bg-red-600 hover:bg-red-700 rounded-xl font-medium text-base px-8 h-14 shadow-lg shadow-red-600/30 hover:shadow-red-600/50 transition-all btn-animated" data-testid="hero-submit-btn">
                    <FileText className="w-5 h-5 mr-2" />
                    लापता व्यक्ति की रिपोर्ट करें
                  </Button>
                </Link>
                <Link to="/search">
                  <Button size="lg" variant="outline" className="rounded-xl font-medium text-base px-8 h-14 border-gray-600 text-white hover:bg-white/10 transition-all" data-testid="hero-search-btn">
                    <Search className="w-5 h-5 mr-2" />
                    खोजें
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            {/* Animated Illustration */}
            <motion.div 
              className="relative flex justify-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="relative">
                {/* Main Search Icon with Animation */}
                <div className="w-64 h-64 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center relative">
                  <motion.div
                    animate={{ rotate: [0, -10, 10, -10, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="relative"
                  >
                    <Search className="w-32 h-32 text-white/20" strokeWidth={1} />
                    <motion.div
                      className="absolute inset-0"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Search className="w-32 h-32 text-red-500/50" strokeWidth={1} />
                    </motion.div>
                  </motion.div>
                  
                  {/* Pulse Ring */}
                  <div className="absolute inset-0 rounded-full border-2 border-red-500/30 animate-pulse-ring" />
                  <div className="absolute inset-0 rounded-full border border-red-500/20 animate-pulse-ring" style={{ animationDelay: '0.5s' }} />
                </div>

                {/* Floating Elements */}
                <motion.div 
                  className="absolute -top-4 -right-4 w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center shadow-lg glow-red"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                >
                  <AlertTriangle className="w-8 h-8 text-white" />
                </motion.div>
                
                <motion.div 
                  className="absolute -bottom-2 -left-4 w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg glow-amber"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                >
                  <Users className="w-7 h-7 text-white" />
                </motion.div>
                
                <motion.div 
                  className="absolute top-1/2 -right-8 w-12 h-12 bg-violet-500 rounded-lg flex items-center justify-center shadow-lg"
                  animate={{ x: [0, 5, 0], rotate: [0, 10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                >
                  <ScanFace className="w-6 h-6 text-white" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== MOBILE HERO ===== */}
      <section className="md:hidden hero-gradient text-white p-6 pb-12 rounded-b-[2rem]">
        <div className="flex items-center gap-2 mb-6">
          <img src="/gumsuda-logo.png" alt="गुमसुदा" className="h-10 w-auto object-contain" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-outfit font-black text-3xl leading-tight mb-3">
            परिवारों को मिलाने में<br />
            <span className="text-red-400">मदद करें</span>
          </h1>
          <p className="text-white/70 text-sm leading-relaxed">
            हर जानकारी महत्वपूर्ण है। साथ मिलकर हम लापता व्यक्तियों को घर वापस ला सकते हैं।
          </p>
        </motion.div>

        {/* Mobile Animated Magnifying Glass - same style as desktop */}
        <motion.div 
          className="mt-6 flex justify-center"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <div className="relative">
            {/* Main Search Circle */}
            <div className="w-36 h-36 bg-gradient-to-br from-white/10 to-white/5 rounded-full flex items-center justify-center relative">
              <motion.div
                animate={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="relative"
              >
                <Search className="w-16 h-16 text-white/20" strokeWidth={1} />
                <motion.div
                  className="absolute inset-0"
                  animate={{ scale: [1, 1.15, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Search className="w-16 h-16 text-red-500/50" strokeWidth={1} />
                </motion.div>
              </motion.div>

              {/* Pulse Rings */}
              <div className="absolute inset-0 rounded-full border-2 border-red-500/30 animate-pulse-ring" />
              <div className="absolute inset-0 rounded-full border border-red-500/15 animate-pulse-ring" style={{ animationDelay: '0.5s' }} />
            </div>

            {/* Floating Elements */}
            <motion.div 
              className="absolute -top-3 -right-3 w-11 h-11 bg-red-500 rounded-xl flex items-center justify-center shadow-lg glow-red"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <AlertTriangle className="w-5 h-5 text-white" />
            </motion.div>
            
            <motion.div 
              className="absolute -bottom-2 -left-3 w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center shadow-lg glow-amber"
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            >
              <Users className="w-5 h-5 text-white" />
            </motion.div>
            
            <motion.div 
              className="absolute top-1/2 -right-6 w-9 h-9 bg-violet-500 rounded-lg flex items-center justify-center shadow-lg"
              animate={{ x: [0, 5, 0], rotate: [0, 10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            >
              <ScanFace className="w-4 h-4 text-white" />
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ===== STATS SECTION ===== */}
      <section className="px-4 md:px-0 md:py-12 -mt-6 md:mt-0">
        <div className="md:max-w-7xl md:mx-auto md:px-6">
          {/* Desktop Stats */}
          <div className="hidden md:grid md:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="app-card p-6 card-hover"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <span className="text-sm font-medium text-gray-500">लापता व्यक्ति</span>
              </div>
              <p className="font-outfit font-black text-4xl text-red-600">{stats.total_missing}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="app-card p-6 card-hover"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-amber-600" />
                </div>
                <span className="text-sm font-medium text-gray-500">अज्ञात व्यक्ति</span>
              </div>
              <p className="font-outfit font-black text-4xl text-amber-600">{stats.total_unknown}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="app-card p-6 card-hover"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-gray-500">लंबित रिपोर्ट</span>
              </div>
              <p className="font-outfit font-black text-4xl text-blue-600">{stats.pending_approval}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="app-card p-6 card-hover"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Search className="w-6 h-6 text-green-600" />
                </div>
                <span className="text-sm font-medium text-gray-500">स्वीकृत केस</span>
              </div>
              <p className="font-outfit font-black text-4xl text-green-600">{stats.approved_cases}</p>
            </motion.div>
          </div>

          {/* Mobile Stats */}
          <div className="md:hidden grid grid-cols-2 gap-3">
            <Link to="/missing" data-testid="stat-missing-card">
              <motion.div 
                className="stat-card-missing p-4 rounded-2xl"
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="text-xs font-medium text-red-600">लापता</span>
                </div>
                <p className="font-outfit font-black text-3xl text-red-700">{stats.total_missing}</p>
                <p className="text-xs text-red-600/70 mt-1">व्यक्ति दर्ज</p>
              </motion.div>
            </Link>

            <Link to="/unknown" data-testid="stat-unknown-card">
              <motion.div 
                className="stat-card-unknown p-4 rounded-2xl"
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-amber-600" />
                  <span className="text-xs font-medium text-amber-600">अज्ञात</span>
                </div>
                <p className="font-outfit font-black text-3xl text-amber-700">{stats.total_unknown}</p>
                <p className="text-xs text-amber-600/70 mt-1">व्यक्ति पाए गए</p>
              </motion.div>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FEATURES SECTION ===== */}
      <section className="px-4 md:px-0 mt-6 md:mt-0 md:py-12 md:bg-gray-50">
        <div className="md:max-w-7xl md:mx-auto md:px-6">
          <div className="hidden md:block text-center mb-10">
            <h2 className="font-outfit font-bold text-3xl text-gray-900 mb-3">हमारी सेवाएं</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">लापता व्यक्तियों की खोज और अज्ञात व्यक्तियों की पहचान में मदद के लिए व्यापक प्लेटफ़ॉर्म</p>
          </div>

          {/* Desktop Features Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="app-card p-8 group"
            >
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-red-600 transition-colors">
                <AlertTriangle className="w-8 h-8 text-red-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-outfit font-bold text-xl text-gray-900 mb-3">लापता व्यक्ति खोजें</h3>
              <p className="text-gray-600 mb-6">लापता व्यक्तियों की सूची देखें, फ़िल्टर करें और जानकारी प्रदान करें</p>
              <Link to="/missing" className="inline-flex items-center text-red-600 font-medium hover:underline">
                विस्तृत जानकारी <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -5 }}
              className="app-card p-8 group"
            >
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-amber-600 transition-colors">
                <Users className="w-8 h-8 text-amber-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-outfit font-bold text-xl text-gray-900 mb-3">अज्ञात व्यक्ति देखें</h3>
              <p className="text-gray-600 mb-6">पाए गए अज्ञात व्यक्तियों की जानकारी और पहचान में मदद करें</p>
              <Link to="/unknown" className="inline-flex items-center text-amber-600 font-medium hover:underline">
                विस्तृत जानकारी <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -5 }}
              className="app-card p-8 group"
            >
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-green-600 transition-colors">
                <FileText className="w-8 h-8 text-green-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-outfit font-bold text-xl text-gray-900 mb-3">रिपोर्ट दर्ज करें</h3>
              <p className="text-gray-600 mb-6">लापता या अज्ञात व्यक्ति की जानकारी सरलता से दर्ज करें</p>
              <Link to="/submit" className="inline-flex items-center text-green-600 font-medium hover:underline">
                रिपोर्ट दर्ज करें <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </motion.div>
          </div>

          {/* Mobile Quick Actions */}
          <div className="md:hidden space-y-3">
            <h2 className="font-outfit font-semibold text-lg mb-4">त्वरित कार्रवाई</h2>
            
            <Link to="/submit" data-testid="action-report">
              <motion.div 
                className="app-card p-4 flex items-center gap-4"
                whileTap={{ scale: 0.98 }}
              >
                <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">रिपोर्ट दर्ज करें</h3>
                  <p className="text-sm text-gray-500">लापता या अज्ञात व्यक्ति की जानकारी</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </motion.div>
            </Link>

            <Link to="/search" data-testid="action-search">
              <motion.div 
                className="app-card p-4 flex items-center gap-4"
                whileTap={{ scale: 0.98 }}
              >
                <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Search className="w-6 h-6 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">केस खोजें</h3>
                  <p className="text-sm text-gray-500">नाम, शहर या राज्य से खोजें</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </motion.div>
            </Link>

            <Link to="/face-match" data-testid="action-face-match">
              <motion.div 
                className="app-card p-4 flex items-center gap-4 bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200"
                whileTap={{ scale: 0.98 }}
              >
                <div className="w-12 h-12 bg-violet-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <ScanFace className="w-6 h-6 text-violet-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">AI चेहरा मिलान</h3>
                  <p className="text-sm text-gray-500">फोटो से मिलते-जुलते चेहरे खोजें</p>
                </div>
                <ChevronRight className="w-5 h-5 text-violet-400" />
              </motion.div>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== AI FEATURE BANNER (Desktop) ===== */}
      <section className="hidden md:block py-12">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="bg-gradient-to-r from-violet-600 via-indigo-600 to-violet-600 rounded-3xl p-10 text-white relative overflow-hidden animate-gradient"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10 flex items-center gap-10">
              <motion.div 
                className="w-28 h-28 bg-white/20 rounded-3xl flex items-center justify-center flex-shrink-0"
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <ScanFace className="w-14 h-14" />
              </motion.div>
              <div className="flex-1">
                <h3 className="font-outfit font-bold text-3xl mb-3">AI चेहरा मिलान</h3>
                <p className="text-white/80 text-lg mb-6 max-w-xl">
                  फोटो अपलोड करें और हमारे उन्नत AI की मदद से डेटाबेस में मिलते-जुलते चेहरों की खोज करें
                </p>
                <Link to="/face-match">
                  <Button size="lg" className="bg-white text-violet-600 hover:bg-gray-100 rounded-xl font-medium px-8 btn-animated">
                    अभी खोजें
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== SHOKH SANDESH SECTION ===== */}
      {shokhItems.length > 0 && (
        <section className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Heart className="w-6 h-6 text-gray-800" fill="currentColor" />
                <h2 className="font-outfit font-bold text-xl md:text-2xl text-gray-900">शोक संदेश</h2>
                {shokhCount > 0 && <span className="text-sm text-gray-500">({shokhCount})</span>}
              </div>
              <Link to="/shokh-sandesh" className="text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1">
                सभी देखें <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {shokhItems.map((item, idx) => (
                <motion.div key={item.sandesh_id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}>
                  <Link to={`/shokh-sandesh/${item.sandesh_id}`} className="block">
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <div className="relative aspect-square bg-gray-100">
                        {item.photo_url ? (
                          <img src={getPhotoUrl(item.photo_url)} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Heart className="w-10 h-10 text-gray-300" /></div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-2 left-2 right-2 text-white">
                          <h3 className="font-outfit font-bold text-sm md:text-base leading-tight truncate">{item.name}</h3>
                          <p className="text-xs opacity-80">{item.city}</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Mobile Shokh Sandesh Quick Action */}
      <div className="md:hidden px-4 mb-4">
        <Link to="/shokh-sandesh/submit">
          <motion.div className="app-card p-4 flex items-center gap-4" whileTap={{ scale: 0.98 }}>
            <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Heart className="w-6 h-6 text-gray-700" fill="currentColor" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900">शोक संदेश दें</h3>
              <p className="text-sm text-gray-500">श्रद्धांजलि दर्ज करें</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </motion.div>
        </Link>
      </div>

      {/* ===== DESKTOP FOOTER ===== */}
      <footer className="hidden md:block py-12 bg-gray-900 text-white mt-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center">
                  <span className="font-outfit font-black">G</span>
                </div>
                <span className="font-outfit font-bold text-xl">गुमसुदा</span>
              </div>
              <p className="text-gray-400 text-sm">
                लापता और अज्ञात व्यक्तियों की खोज में मदद करने वाला राष्ट्रीय पोर्टल
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">लिंक</h4>
              <div className="space-y-2 text-gray-400">
                <Link to="/missing" className="block hover:text-white transition-colors">लापता व्यक्ति</Link>
                <Link to="/unknown" className="block hover:text-white transition-colors">अज्ञात व्यक्ति</Link>
                <Link to="/submit" className="block hover:text-white transition-colors">रिपोर्ट दर्ज करें</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">सुविधाएं</h4>
              <div className="space-y-2 text-gray-400">
                <Link to="/search" className="block hover:text-white transition-colors">खोजें</Link>
                <Link to="/face-match" className="block hover:text-white transition-colors">AI चेहरा मिलान</Link>
                <Link to="/admin" className="block hover:text-white transition-colors">Admin</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">संपर्क</h4>
              <div className="space-y-2 text-gray-400">
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  +91 7417074121
                </p>
                <Link to="/privacy-policy" className="block hover:text-white transition-colors">गोपनीयता नीति</Link>
                <Link to="/terms" className="block hover:text-white transition-colors">नियम और शर्तें</Link>
                <Link to="/safety" className="block hover:text-white transition-colors">Child Safety</Link>
                <Link to="/request-delete" className="block hover:text-white transition-colors">लिस्टिंग हटाएं</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            <p>© 2026 गुमसुदा पोर्टल। सर्वाधिकार सुरक्षित।</p>
            <div className="flex items-center justify-center gap-4 mt-2 flex-wrap">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <span>|</span>
              <Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
              <span>|</span>
              <Link to="/safety" className="hover:text-white transition-colors">Child Safety</Link>
              <span>|</span>
              <Link to="/request-delete" className="hover:text-white transition-colors">Request Deletion</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile Bottom Spacing */}
      <div className="h-24 md:hidden" />
    </div>
  );
};

export default HomePage;
